import { ITEMS_PER_PAGE } from '@mais-retorno-front/constants/items-per-page';
import { AxiosInstance } from 'axios';

interface INewsByAuthor {
  newsByAuthor: {
    title: string;
    slug: string;
    excerpt: string;
    link: string;
    contentId: number;
    date: string;
    chapeu: string;
    type: string;
    imageUrl: string;
    imageTitle: string | null;
    imageAltText: string | null;
    categories: { nodes: { name: string }[] };
  }[];
  pageInfo: {
    pageCount: number;
  };
  authorInfo: {
    slug: string;
    name: string;
    link: string;
  };
}

interface IResponse {
  data: {
    noticias: {
      data: {
        id: number;
        attributes: {
          title: string;
          chapeu: string;
          slug: string;
          alternative_title: string;
          updatedAt: string;
          publishedAt: string;
          excerpt: string;
          path: string;
          thumbnail: {
            data: {
              attributes: {
                name: string;
                alternativeText: string;
                url: string;
              };
            };
          };
          author: {
            data: {
              attributes: {
                name: string;
              };
            };
          };
          categories: {
            data: {
              attributes: {
                title: string;
              };
            }[];
          };
        };
      }[];
      meta: {
        pagination: {
          pageCount: number;
        };
      };
    };
    usersPermissionsUsers: {
      data: {
        attributes: {
          name: string;
          path: string;
          slug: string;
        };
      }[];
    };
  };
}

export class GetNewsByAuthorUseCase {
  constructor(private readonly http: AxiosInstance) {}

  async execute(authorSlug: string, page = 1): Promise<INewsByAuthor> {
    const { data } = await this.http.post<IResponse>('', {
      query: `
      query newsByAuthor {
        noticias(filters: { author : { slug: {eq: "${authorSlug}" }}}, pagination: {page: ${page}, pageSize: ${ITEMS_PER_PAGE.newsByCategory}}, sort: "publishedAt:desc") {
          data {
            id
            attributes {
              title
              chapeu
              slug
              alternative_title
              updatedAt
              publishedAt
              excerpt
              path
              thumbnail {
                data {
                  attributes {
                    name
                    alternativeText
                    url
                  }
                }
              }
              author {
                data {
                  attributes {
                    name
                  }
                }
              }
              categories {
                data {
                  attributes {
                    title
                  }
                }
              }
            }
          }
          meta {
            pagination {
              pageCount
            }
          }
        }
        usersPermissionsUsers(filters: { slug: {eq: "${authorSlug}"}}) {
          data {
            attributes {
              name
              path
              slug
            }
          }
        }
      }
      `,
    });

    const author = data?.data?.usersPermissionsUsers?.data[0];

    if (!author) {
      return {
        newsByAuthor: [],
        pageInfo: null,
        authorInfo: null,
      };
    }

    const authorInfo = {
      link: `${process.env.NEXT_PUBLIC_DOMAIN}${author?.attributes?.path}/${author?.attributes?.slug}`,
      name: author?.attributes?.name || null,
      slug: author?.attributes?.slug || null,
    };

    if (
      !data?.data?.noticias?.data ||
      data?.data?.noticias?.data.length === 0
    ) {
      return {
        newsByAuthor: [],
        pageInfo: {
          pageCount: 0,
        },
        authorInfo,
      };
    }

    const newsContent = data?.data?.noticias?.data.map((news) => {
      return {
        title: news?.attributes?.title || null,
        slug: news?.attributes?.slug || null,
        excerpt: news?.attributes?.excerpt || null,
        link: `${process.env.NEXT_PUBLIC_DOMAIN}${news.attributes.path}/${news.attributes.slug}`,
        contentId: news?.id,
        date: news?.attributes?.publishedAt || null,
        chapeu:
          news?.attributes?.chapeu ||
          news?.attributes?.categories?.data[0]?.attributes?.title ||
          null,
        type: 'post',
        imageUrl:
          news?.attributes?.thumbnail?.data?.attributes?.url ||
          '/img/placeholder.png',
        imageTitle:
          news?.attributes?.thumbnail?.data?.attributes?.name ||
          news?.attributes?.thumbnail?.data?.attributes?.alternativeText ||
          null,
        imageAltText:
          news?.attributes?.thumbnail?.data?.attributes?.alternativeText ||
          news?.attributes?.thumbnail?.data?.attributes?.name ||
          null,
        categories: {
          nodes: news.attributes.categories.data.map((category) => {
            return { name: category?.attributes?.title || null };
          }),
        },
      };
    });

    return {
      newsByAuthor: newsContent,
      pageInfo: {
        pageCount: data.data?.noticias?.meta?.pagination?.pageCount || null,
      },
      authorInfo,
    };
  }
}
